import { useCallback, useEffect, useRef } from "react";

/**
 * CanvasRoutes — animated glowing route lines drawn on a <canvas> overlay.
 *
 * Inspired by Nash's `nashRoutesHero` canvas animation. Draws flowing particle
 * trails that simulate logistics/data routes across the viewport.
 *
 * Features:
 * - Multiple route paths with bezier curves
 * - Glowing particle heads that travel along paths
 * - Fading trail effect behind each particle
 * - Cursor reactivity (particles subtly deflect toward cursor)
 * - Smooth 60fps via requestAnimationFrame
 * - Respects reduced-motion preference
 *
 * Positioned as an absolute overlay with pointer-events: none.
 * Uses mask-image to fade out at the bottom edge (Nash-style).
 */

interface RouteParticle {
  /** Progress along the path (0 → 1) */
  t: number;
  /** Speed multiplier */
  speed: number;
  /** Trail length (number of previous positions to draw) */
  trailLength: number;
  /** Color of the particle glow */
  color: string;
  /** Opacity of the particle */
  opacity: number;
  /** Previous positions for trail rendering */
  trail: Array<{ x: number; y: number }>;
  /** Path index this particle follows */
  pathIndex: number;
}

/** Bezier control points for a route path (normalized 0-1 coordinates) */
interface RoutePath {
  points: Array<{ x: number; y: number }>;
}

// Route colors adapted to our brand palette
const ROUTE_COLORS = [
  "rgba(129, 129, 255, 0.6)",   // brand purple
  "rgba(34, 211, 238, 0.5)",    // cyan
  "rgba(110, 90, 196, 0.5)",    // deep purple
  "rgba(253, 147, 110, 0.4)",   // brand orange
  "rgba(157, 217, 200, 0.4)",   // teal
  "rgba(79, 141, 168, 0.5)",    // steel blue
  "rgba(201, 255, 0, 0.3)",     // chartreuse (Nash accent)
  "rgba(110, 207, 217, 0.4)",   // aqua
];

// Generate smooth bezier route paths (normalized 0-1 space)
function generateRoutePaths(count: number): RoutePath[] {
  const paths: RoutePath[] = [];
  for (let i = 0; i < count; i++) {
    const numPoints = 5 + Math.floor(Math.random() * 3); // 5-7 control points
    const points: Array<{ x: number; y: number }> = [];

    // Routes flow generally from left-to-right or top-to-bottom
    const isVertical = Math.random() > 0.4;

    for (let j = 0; j < numPoints; j++) {
      const progress = j / (numPoints - 1);
      if (isVertical) {
        points.push({
          x: 0.1 + Math.random() * 0.8 + Math.sin(progress * Math.PI * 2) * 0.15,
          y: -0.1 + progress * 1.2,
        });
      } else {
        points.push({
          x: -0.1 + progress * 1.2,
          y: 0.1 + Math.random() * 0.8 + Math.cos(progress * Math.PI * 2) * 0.15,
        });
      }
    }
    paths.push({ points });
  }
  return paths;
}

/** Catmull-Rom spline interpolation for smooth curves through control points */
function catmullRom(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  p3: { x: number; y: number },
  t: number,
): { x: number; y: number } {
  const t2 = t * t;
  const t3 = t2 * t;

  const x =
    0.5 *
    (2 * p1.x +
      (-p0.x + p2.x) * t +
      (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
      (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3);

  const y =
    0.5 *
    (2 * p1.y +
      (-p0.y + p2.y) * t +
      (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
      (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3);

  return { x, y };
}

/** Get position along a path at progress t (0-1) using Catmull-Rom */
function getPointOnPath(path: RoutePath, t: number): { x: number; y: number } {
  const points = path.points;
  const n = points.length - 1;
  const segment = Math.min(Math.floor(t * n), n - 1);
  const localT = (t * n) - segment;

  const p0 = points[Math.max(0, segment - 1)];
  const p1 = points[segment];
  const p2 = points[Math.min(n, segment + 1)];
  const p3 = points[Math.min(n, segment + 2)];

  return catmullRom(p0, p1, p2, p3, localT);
}

/** Create initial particles distributed across paths */
function createParticles(paths: RoutePath[], count: number): RouteParticle[] {
  const particles: RouteParticle[] = [];
  for (let i = 0; i < count; i++) {
    const pathIndex = i % paths.length;
    particles.push({
      t: Math.random(), // random start position
      speed: 0.0003 + Math.random() * 0.0006, // varied speeds
      trailLength: 20 + Math.floor(Math.random() * 40), // 20-60 trail points
      color: ROUTE_COLORS[i % ROUTE_COLORS.length],
      opacity: 0.3 + Math.random() * 0.5,
      trail: [],
      pathIndex,
    });
  }
  return particles;
}

export function CanvasRoutes() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const pathsRef = useRef<RoutePath[]>([]);
  const particlesRef = useRef<RouteParticle[]>([]);
  const reducedMotionRef = useRef(false);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.scale(dpr, dpr);
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { width, height } = canvas.getBoundingClientRect();
    const paths = pathsRef.current;
    const particles = particlesRef.current;
    const mouse = mouseRef.current;

    // Clear with slight fade for trail persistence
    ctx.clearRect(0, 0, width, height);

    // Draw each particle and its trail
    for (const particle of particles) {
      // Advance particle along path
      particle.t += particle.speed;
      if (particle.t > 1) {
        particle.t = 0;
        particle.trail = [];
      }

      const path = paths[particle.pathIndex];
      if (!path) continue;

      // Get current position
      const pos = getPointOnPath(path, particle.t);

      // Apply subtle cursor deflection
      const dx = (mouse.x - pos.x) * 0.02;
      const dy = (mouse.y - pos.y) * 0.02;
      const screenX = (pos.x + dx) * width;
      const screenY = (pos.y + dy) * height;

      // Add to trail
      particle.trail.push({ x: screenX, y: screenY });
      if (particle.trail.length > particle.trailLength) {
        particle.trail.shift();
      }

      // Draw trail
      if (particle.trail.length > 2) {
        ctx.beginPath();
        ctx.moveTo(particle.trail[0].x, particle.trail[0].y);

        for (let i = 1; i < particle.trail.length; i++) {
          const prev = particle.trail[i - 1];
          const curr = particle.trail[i];
          const midX = (prev.x + curr.x) / 2;
          const midY = (prev.y + curr.y) / 2;
          ctx.quadraticCurveTo(prev.x, prev.y, midX, midY);
        }

        // Gradient trail: fades from transparent to particle color
        const gradient = ctx.createLinearGradient(
          particle.trail[0].x,
          particle.trail[0].y,
          screenX,
          screenY,
        );
        gradient.addColorStop(0, "rgba(0, 0, 0, 0)");
        gradient.addColorStop(0.7, particle.color);
        gradient.addColorStop(1, particle.color);

        ctx.strokeStyle = gradient;
        ctx.lineWidth = 1.5;
        ctx.globalAlpha = particle.opacity;
        ctx.stroke();
      }

      // Draw glowing particle head
      ctx.beginPath();
      ctx.arc(screenX, screenY, 2, 0, Math.PI * 2);
      ctx.fillStyle = particle.color;
      ctx.globalAlpha = particle.opacity * 1.5;
      ctx.fill();

      // Outer glow
      ctx.beginPath();
      ctx.arc(screenX, screenY, 5, 0, Math.PI * 2);
      ctx.fillStyle = particle.color;
      ctx.globalAlpha = particle.opacity * 0.3;
      ctx.fill();

      ctx.globalAlpha = 1;
    }

    rafRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    // Check reduced motion preference
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedMotionRef.current = mq.matches;
    if (mq.matches) return;

    // Generate paths and particles
    const numPaths = 10;
    const numParticles = 16;
    pathsRef.current = generateRoutePaths(numPaths);
    particlesRef.current = createParticles(pathsRef.current, numParticles);

    // Setup canvas
    resize();
    window.addEventListener("resize", resize, { passive: true });

    // Mouse tracking
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = {
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      };
    };
    window.addEventListener("mousemove", handleMouseMove, { passive: true });

    // Start animation
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [animate, resize]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="canvas-routes"
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 1,
        maskImage:
          "linear-gradient(black 0px, black calc(100% - 120px), transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(black 0px, black calc(100% - 120px), transparent 100%)",
        opacity: 0.4,
      }}
    />
  );
}
